import { bindable, customElement, resolve } from 'aurelia';
import { I18N } from '@aurelia/i18n';
import template from './collection-picker.html';
import type { Collection, CollectionRef, CollectionTreeNode, MediaItem } from '../services/api.types';
import { getCollectionTree, getMediaPreviewUrl } from '../services/api.service';
import type { CollectionTreeItem } from './collection-tree-node';

/** Modal popup for choosing a target collection to add or move media items to.
 *  Shows the complete collection hierarchy and quick-creates a new collection inside
 *  the selected node. The hierarchy is loaded as one lightweight flat request, while
 *  collection metadata needed for suggestions and confirmation is included in each node. */
@customElement({ name: 'collection-picker', template })
export class CollectionPicker {
  private readonly i18n: I18N = resolve(I18N);

  @bindable public selected!: (collection: CollectionRef) => void;
  @bindable public closed!: () => void;
  @bindable public selectedMediaItems: MediaItem[] = [];
  @bindable public mode: 'add' | 'move' = 'add';
  @bindable public excludedCollectionId: string | null = null;

  private readonly _collectionsById = new Map<string, CollectionPickerCollection>();
  public treeRoots: CollectionTreeItem[] = [];
  public treeRoot: CollectionTreeItem | null = null;
  public treeEl: HTMLElement | null = null;
  public allCollections: CollectionPickerCollection[] = [];
  public selectedCollection: CollectionPickerCollection | null = null;
  public selectedCollectionId: string | null = null;
  public selectedNodeId: string | null = null;
  public collectionSearchText = '';
  public sortOption = 'capturedat:desc';
  public pendingCollection: CollectionPickerCollection | null = null;
  public pendingItemCount = 0;
  public suggestionId = '';
  public loading = false;
  public showCreateModal = false;
  public showConfirmation = false;
  public error = '';
  private readonly _rootNodeId = '__collection-picker-root__';
  private readonly _expandedIds = new Set<string>();
  private _rootExpanded = true;

  public get dialogTitleKey(): string {
    return this.mode === 'move' ? 'collections.picker.moveTitle' : 'collections.picker.title';
  }

  public get actionKey(): string {
    return this.mode === 'move' ? 'collections.picker.moveHere' : 'collections.picker.addHere';
  }

  public get confirmationTitleKey(): string {
    return this.mode === 'move'
      ? 'collections.picker.confirmMoveTitle'
      : 'collections.picker.confirmAddTitle';
  }

  public get confirmationItemsKey(): string {
    return this.mode === 'move'
      ? 'collections.picker.itemsToMove'
      : 'collections.picker.itemsToAdd';
  }

  public get createActionTitle(): string {
    return this.selectedCollection
      ? this.i18n.tr('collections.picker.createInside', { title: this.selectedCollection.title })
      : this.i18n.tr('collections.picker.createHere');
  }

  public get hasCollectionSearch(): boolean {
    return this.collectionSearchText.trim().length > 0;
  }

  public get suggestedCollections(): CollectionPickerCollection[] {
    const mediaDates = this.selectedMediaItems
      .map(item => this._datePart(item.capturedAt))
      .filter((date): date is string => date !== null);
    if (mediaDates.length === 0) return [];

    return this.allCollections
      .filter(collection => collection.id !== this.excludedCollectionId
        && collection.id !== this.selectedCollectionId)
      .filter(collection => {
        const from = this._datePart(collection.collectionDate);
        const to = this._datePart(collection.collectionDateTo) ?? from;
        if (!from || !to) return false;
        return mediaDates.some(date => date >= from && date <= to);
      });
  }

  public async attached(): Promise<void> {
    await this._loadRoot();
  }

  public close(): void { this.closed(); }

  public pickSelected(): void {
    if (this.selectedCollection) this.requestSelection(this.selectedCollection);
  }

  public pickSuggested(collection: CollectionPickerCollection): void {
    this.requestSelection(collection);
  }

  public pickSuggestedById(): void {
    if (!this.suggestionId) return;
    const collection = this.suggestedCollections.find(item => item.id === this.suggestionId);
    if (collection) this.pickSuggested(collection);
  }

  public getMediaPreviewUrl(collection: CollectionPickerCollection): string {
    if (!collection.coverMediaItemId) return '';
    return getMediaPreviewUrl(collection.coverMediaItemId, collection.lastUpdatedAt);
  }

  public cancelConfirmation(): void {
    this.pendingCollection = null;
    this.pendingItemCount = 0;
    this.suggestionId = '';
    this.showConfirmation = false;
  }

  public confirmPendingSelection(): void {
    const collection = this.pendingCollection;
    if (!collection) return;
    this.pendingCollection = null;
    this.pendingItemCount = 0;
    this.showConfirmation = false;
    this.selected(collection);
  }

  public selectRoot(): void {
    this.selectedCollection = null;
    this.selectedCollectionId = null;
    this.selectedNodeId = this._rootNodeId;
    this.treeRoots = [...this.treeRoots];
  }

  public selectTreeNode = (node: CollectionTreeItem): void => {
    if (node.id === this._rootNodeId) {
      this.selectRoot();
      return;
    }
    const collection = this._collectionsById.get(node.id);
    if (!collection || collection.id === this.excludedCollectionId) return;
    this.selectedCollection = collection;
    this.selectedCollectionId = collection.id;
    this.selectedNodeId = collection.id;
    this.treeRoots = [...this.treeRoots];
  };

  public toggleTreeNode = (node: CollectionTreeItem, event?: Event): void => {
    event?.stopPropagation();
    if (this.hasCollectionSearch) return;

    node.expanded = !node.expanded;
    if (node.id === this._rootNodeId) {
      this._rootExpanded = node.expanded;
    } else if (node.expanded) {
      this._expandedIds.add(node.id);
    } else {
      this._expandedIds.delete(node.id);
    }
    this.treeRoots = [...this.treeRoots];
  };

  public expandAll(): void {
    for (const collection of this.allCollections) {
      if (collection.id !== this.excludedCollectionId)
        this._expandedIds.add(collection.id);
    }
    this._rootExpanded = true;
    this._rebuildTree();
  }

  public collapseAll(): void {
    this._expandedIds.clear();
    this._rootExpanded = true;
    this._rebuildTree();
  }

  public onCollectionSearchInput(event?: Event): void {
    const target = event?.target;
    if (target instanceof HTMLInputElement)
      this.collectionSearchText = target.value;
    this._rebuildTree();
  }

  public clearCollectionSearch(): void {
    if (!this.collectionSearchText) return;
    this.collectionSearchText = '';
    this._rebuildTree();
  }

  public openCreateAt = (node: CollectionTreeItem, event?: Event): void => {
    event?.stopPropagation();
    if (node.id === this._rootNodeId) this.selectRoot();
    else this.selectTreeNode(node);
    this.showCreateModal = true;
  };

  public async onCreateSaved(collection: Collection): Promise<void> {
    if (!this.allCollections.some(existing => existing.id === collection.id)) {
      this.allCollections = [...this.allCollections, collection];
      this._collectionsById.set(collection.id, collection);
    }
    this._rebuildTree();
    this.selectedCollection = collection;
    this.selectedCollectionId = collection.id;
    this.selectedNodeId = collection.id;
    this._expandPathTo(collection.id);
    this.showCreateModal = false;
    this._scrollToCollection(collection.id);
  }

  public onCreateClosed(): void {
    this.showCreateModal = false;
  }

  private requestSelection(collection: CollectionPickerCollection): void {
    if (this.showConfirmation) return;
    this.pendingCollection = collection;
    this.pendingItemCount = this.selectedMediaItems.length;
    this.showConfirmation = true;
  }

  private async _loadRoot(): Promise<void> {
    this.loading = true;
    this.error = '';
    this._collectionsById.clear();
    this._expandedIds.clear();
    this._rootExpanded = true;
    try {
      const nodes = await getCollectionTree();
      this.allCollections = nodes.map(node => this._toPickerCollection(node));
      for (const collection of this.allCollections)
        this._collectionsById.set(collection.id, collection);
      this._rebuildTree();
      this.selectRoot();
    } catch (e) {
      this.error = e instanceof Error ? e.message : String(e);
    } finally {
      this.loading = false;
    }
  }

  private _toPickerCollection(node: CollectionTreeNode): CollectionPickerCollection {
    return {
      id: node.id,
      title: node.title,
      subtitle: node.subtitle,
      collectionDate: node.collectionDate,
      collectionDateTo: node.collectionDateTo,
      parentId: node.parentId,
      coverMediaItemId: node.coverMediaItemId,
      lastUpdatedAt: node.lastUpdatedAt,
      coverPreviewUrl: node.coverMediaItemId
        ? getMediaPreviewUrl(node.coverMediaItemId, node.lastUpdatedAt)
        : null,
    };
  }

  private _rebuildTree(): void {
    const visibleCollections = this.allCollections
      .filter(collection => collection.id !== this.excludedCollectionId);
    const visibleIds = new Set(visibleCollections.map(collection => collection.id));
    const nodes = new Map<string, CollectionTreeItem>();

    for (const collection of visibleCollections) {
      nodes.set(collection.id, {
        id: collection.id,
        title: collection.title,
        subtitle: collection.subtitle,
        parentId: collection.parentId,
        children: [],
        expanded: false,
        collectionDate: collection.collectionDate,
        collectionDateTo: collection.collectionDateTo,
        coverPreviewUrl: collection.coverPreviewUrl,
      });
    }

    let roots: CollectionTreeItem[] = [];
    for (const collection of visibleCollections) {
      const node = nodes.get(collection.id);
      if (!node) continue;
      const parent = collection.parentId ? nodes.get(collection.parentId) : undefined;
      if (collection.parentId && (!parent || !visibleIds.has(collection.parentId))) continue;
      if (parent) parent.children.push(node);
      else roots.push(node);
    }

    const sortRecursively = (items: CollectionTreeItem[]): void => {
      items.sort((left, right) => this._compareTreeItems(left, right));
      for (const item of items) sortRecursively(item.children);
    };
    sortRecursively(roots);

    const query = this.collectionSearchText.trim().toLocaleLowerCase();
    if (query) {
      const matchingIds = new Set(
        visibleCollections
          .filter(collection => collection.title.toLocaleLowerCase().includes(query))
          .map(collection => collection.id),
      );
      const includedIds = new Set<string>();

      for (const collection of visibleCollections) {
        if (!matchingIds.has(collection.id)) continue;
        let current: CollectionPickerCollection | undefined = collection;
        while (current && visibleIds.has(current.id)) {
          includedIds.add(current.id);
          current = current.parentId
            ? this._collectionsById.get(current.parentId)
            : undefined;
        }
      }

      const filterTree = (items: CollectionTreeItem[]): CollectionTreeItem[] =>
        items.flatMap(item => {
          if (!includedIds.has(item.id)) return [];
          item.children = filterTree(item.children);
          // A filtered result must remain fully navigable: every ancestor of a match
          // is visible and expanded, even when that ancestor does not match itself.
          item.expanded = true;
          return [item];
        });

      roots = filterTree(roots);
    } else {
      const applyExpansion = (items: CollectionTreeItem[]): void => {
        for (const item of items) {
          item.expanded = this._expandedIds.has(item.id);
          applyExpansion(item.children);
        }
      };
      applyExpansion(roots);
    }

    this.treeRoots = roots;
    this.treeRoot = {
      id: this._rootNodeId,
      title: '',
      parentId: null,
      children: roots,
      expanded: query ? true : this._rootExpanded,
    };
  }

  public sortOptionChanged(): void {
    this._rebuildTree();
  }

  private _compareTreeItems(left: CollectionTreeItem, right: CollectionTreeItem): number {
    const leftCollection = this._collectionsById.get(left.id);
    const rightCollection = this._collectionsById.get(right.id);
    if (!leftCollection || !rightCollection)
      return left.title.localeCompare(right.title, undefined, { sensitivity: 'base' });

    const [sortBy, direction] = this.sortOption.split(':');
    if (sortBy === 'capturedat') {
      const leftDate = this._collectionDate(leftCollection);
      const rightDate = this._collectionDate(rightCollection);
      if (leftDate === null || rightDate === null) {
        if (leftDate === null && rightDate !== null) return -1;
        if (leftDate !== null && rightDate === null) return 1;
      } else if (leftDate !== rightDate) {
        return direction === 'desc'
          ? rightDate - leftDate
          : leftDate - rightDate;
      }
    }

    const titleOrder = left.title.localeCompare(right.title, undefined, {
      sensitivity: 'base',
    });
    return titleOrder || left.id.localeCompare(right.id);
  }

  private _collectionDate(collection: CollectionPickerCollection): number | null {
    if (!collection.collectionDate) return null;
    const timestamp = Date.parse(collection.collectionDate);
    return Number.isFinite(timestamp) ? timestamp : null;
  }

  private _expandPathTo(collectionId: string): void {
    const expandIds = new Set<string>();
    let parentId = this._collectionsById.get(collectionId)?.parentId ?? null;
    while (parentId) {
      expandIds.add(parentId);
      this._expandedIds.add(parentId);
      parentId = this._collectionsById.get(parentId)?.parentId ?? null;
    }
    this._rootExpanded = true;

    const expand = (items: CollectionTreeItem[]): void => {
      for (const item of items) {
        if (!expandIds.has(item.id)) continue;
        item.expanded = true;
        expand(item.children);
      }
    };
    expand(this.treeRoots);
    this.treeRoots = [...this.treeRoots];
  }

  private _scrollToCollection(collectionId: string): void {
    requestAnimationFrame(() => {
      const rows = this.treeEl?.querySelectorAll<HTMLElement>('[data-collection-id]');
      const row = rows
        ? Array.from(rows).find(candidate => candidate.dataset.collectionId === collectionId)
        : undefined;
      row?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    });
  }

  private _datePart(value: string | null): string | null {
    if (!value) return null;
    const datePart = value.substring(0, 10);
    return /^\d{4}-\d{2}-\d{2}$/.test(datePart) ? datePart : null;
  }
}

type CollectionPickerCollection = Pick<
  Collection,
  'id' | 'title' | 'subtitle' | 'collectionDate' | 'collectionDateTo' | 'parentId' | 'coverMediaItemId' | 'lastUpdatedAt'
> & {
  coverPreviewUrl?: string | null;
};
